import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, Download, Loader2, Pencil, RotateCcw, Save, Upload } from "lucide-react";
import * as Y from "yjs";
import { ShapeMapAdapter } from "../../lib/diagram/ShapeMapAdapter";
import { ShapePersistenceGateway } from "../../lib/diagram/shapeGeometry";
import { ScrollArea } from "../ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useDiagramVersions } from "../../hooks/useDiagramVersions";
import type {
  DiagramVersionSummary,
  DiagramVersionDetails,
} from "../../hooks/useDiagramVersions";
import { useDiagramDocument } from "../../contexts/DiagramDocumentContext";
import { extractNodesAndEdgesFromSnapshot, computeDiffHighlights } from "../../utils/snapshotToScene";
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '@/lib/datetime/dateFnsLocale';
import { useMeQuery } from "@/hooks/useMeQuery";
import { useHistoryPreviewStore } from "../../stores/historyPreviewStore";
import { Logger } from "../../utils/logger";
import { useTranslation } from "../../hooks/useTranslation";
import { useLocalBackups } from "../../hooks/useLocalBackups";

const logger = Logger.getLoggerWithPrefix("DiagramHistoryPanel");

function translateVersionLabel(
  label: string | null | undefined,
  translate: (key: string, options?: { defaultValue?: string; version?: string }) => string,
): string {
  if (!label?.trim()) {
    return '';
  }
  const s = label.trim();
  const restored = /^Restored from v(\d+)$/i.exec(s);
  if (restored) {
    return translate('history.labelRestoredFrom', {
      version: restored[1],
      defaultValue: 'Restored from v{{version}}',
    });
  }
  const auto = /^Auto-save v(\d+)$/i.exec(s);
  if (auto) {
    return translate('history.labelAutoSave', {
      version: auto[1],
      defaultValue: 'Auto-save v{{version}}',
    });
  }
  return label;
}

interface DiagramHistoryPanelProps {
  diagramId?: string;
}

export const DiagramHistoryPanel: React.FC<DiagramHistoryPanelProps> = ({
  diagramId,
}) => {
  const { t, i18n } = useTranslation('diagram');
  const { t: tCommon } = useTranslation('common');
  const dateLocale = useMemo(
    () => getDateFnsLocale(i18n.resolvedLanguage ?? i18n.language),
    [i18n.language, i18n.resolvedLanguage],
  );
  const [isRestoring, setIsRestoring] = useState(false);
  const [previewLoadingVersion, setPreviewLoadingVersion] = useState<number | null>(null);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [nameDialogValue, setNameDialogValue] = useState('');
  const [nameDialogMode, setNameDialogMode] = useState<
    { type: 'save' } | { type: 'rename'; version: number }
  >({ type: 'save' });
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { doc } = useDiagramDocument();
  const { versions, createVersion, restoreVersion, renameVersion, getVersionDetails } =
    useDiagramVersions(diagramId);
  const { refetch: refetchVersions } = versions;
  const meQuery = useMeQuery();
  const {
    backups: localBackups,
    isRestoring: isRestoringLocal,
    restore: restoreLocalBackup,
    download: downloadLocalBackup,
  } = useLocalBackups(diagramId, doc);
  const activePreviewVersion = useHistoryPreviewStore((s) => s.activeVersion);
  const setHistoryPreview = useHistoryPreviewStore((s) => s.setPreview);
  const clearHistoryPreview = useHistoryPreviewStore((s) => s.clear);
  const projectionStatus = useHistoryPreviewStore((s) => s.projectionStatus);
  const previewCacheRef = useRef<Map<number, DiagramVersionDetails>>(new Map());
  const userInfo = useMemo(() => {
    return meQuery?.data || { name: 'N/A' }
  }, [meQuery]);

  useEffect(() => {
    return () => {
      clearHistoryPreview();
      previewCacheRef.current.clear();
    };
  }, [clearHistoryPreview]);

  useEffect(() => {
    clearHistoryPreview();
    previewCacheRef.current.clear();
    setPreviewLoadingVersion(null);
  }, [diagramId, clearHistoryPreview]);

  const handleRestore = useCallback(
    async (entry: DiagramVersionSummary) => {
      if (!doc) return;
      try {
        setIsRestoring(true);
        await restoreVersion.mutateAsync(entry.version);
        // Flag so emergency recovery doesn't overwrite the restored snapshot on reload.
        // beforeunload will save pre-restore state to localStorage; this flag tells
        // the recovery logic to discard it.
        if (diagramId) {
          sessionStorage.setItem(`creately-restoring:${diagramId}`, '1');
        }
        window.location.reload();
      } catch (error) {
        logger.error("Failed to restore version", error);
        setIsRestoring(false);
      }
    },
    [doc, diagramId, restoreVersion]
  );

  const handleRename = useCallback(
    (entry: DiagramVersionSummary) => {
      const raw = entry.label ?? '';
      setNameDialogValue(translateVersionLabel(raw, t) || raw);
      setNameDialogMode({ type: 'rename', version: entry.version });
      setNameDialogOpen(true);
    },
    [t],
  );

  const handlePreview = useCallback(
    async (entry: DiagramVersionSummary) => {
      if (!doc || !diagramId) {
        return;
      }

      const { version } = entry;

      if (activePreviewVersion === version) {
        clearHistoryPreview();
        return;
      }

      const requestId = useHistoryPreviewStore.getState().begin();
      setPreviewLoadingVersion(version);
      try {
        let details = previewCacheRef.current.get(version);
        if (!details) {
          details = await getVersionDetails(version);
          if (useHistoryPreviewStore.getState().requestId !== requestId) return;
          previewCacheRef.current.set(version, details);
        }

        if (!details.snapshotBytes) {
          setPreviewLoadingVersion(null);
          clearHistoryPreview();
          return;
        }

        const { nodes, edges } = extractNodesAndEdgesFromSnapshot(details.snapshotBytes);
        const { highlights, ghostNodes } = computeDiffHighlights(nodes, doc);
        const mergedNodes = [...nodes, ...ghostNodes];
        setHistoryPreview(version, mergedNodes, edges, highlights, {
          diagramId, requestId, createdAt: details.createdAt, snapshotBytes: details.snapshotBytes,
        });
      } catch (error) {
        if (useHistoryPreviewStore.getState().requestId !== requestId) return;
        logger.error("Failed to load history preview", error);
        setPreviewLoadingVersion(null);
        clearHistoryPreview();
      } finally {
        if (useHistoryPreviewStore.getState().requestId === requestId) setPreviewLoadingVersion((current) =>
          current === version ? null : current
        );
      }
    },
    [
      doc,
      diagramId,
      activePreviewVersion,
      clearHistoryPreview,
      getVersionDetails,
      setHistoryPreview,
    ]
  );

  const portalTarget = useMemo(() => {
    return typeof document !== "undefined" ? document.body : null;
  }, []);

  // Periodically fetch versions every 2 minutes when panel is mounted
  useEffect(() => {
    if (!diagramId) return;

    // Initial fetch
    refetchVersions();

    // Set up interval for periodic fetching (2 minutes = 120,000ms)
    const interval = setInterval(() => {
      refetchVersions();
    }, 120000);

    // Cleanup interval on unmount
    return () => {
      clearInterval(interval);
    };
  }, [diagramId, refetchVersions]);

  const handleSaveVersion = useCallback(() => {
    setNameDialogValue('');
    setNameDialogMode({ type: 'save' });
    setNameDialogOpen(true);
  }, []);

  const handleNameDialogSubmit = useCallback(() => {
    const trimmed = nameDialogValue.trim();
    if (nameDialogMode.type === 'save') {
      createVersion.mutate({ label: trimmed || undefined });
    } else {
      renameVersion.mutate({
        version: nameDialogMode.version,
        label: trimmed.length > 0 ? trimmed : null,
      });
    }
    setNameDialogOpen(false);
  }, [nameDialogValue, nameDialogMode, createVersion, renameVersion]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleRestoreFromFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !doc) return;
      e.target.value = '';
      try {
        setIsRestoring(true);
        const buffer = await file.arrayBuffer();
        const update = new Uint8Array(buffer);
        const candidateDoc = new Y.Doc();
        Y.applyUpdate(candidateDoc, update);
        const candidateShapes = new ShapeMapAdapter(candidateDoc.getMap('shapes'));
        const candidateRecords: Record<string, unknown>[] = [];
        candidateShapes.forEach((shape, shapeId) => candidateRecords.push({ ...shape, id: shapeId }));
        new ShapePersistenceGateway(candidateShapes).validateCreates(candidateRecords);
        candidateDoc.destroy();
        // Clear doc first — Y.applyUpdate merges (CRDT), can't go backwards
        doc.transact(() => {
          new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes'))).clear();
          doc.getMap('connectors').clear();
        }, 'file-restore');
        Y.applyUpdate(doc, update);
      } catch (error) {
        logger.error('Failed to restore from file', error);
      } finally {
        setIsRestoring(false);
      }
    },
    [doc],
  );

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-foreground">{t('history.title', { defaultValue: 'Version History' })}</div>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSaveVersion}
          disabled={createVersion.isPending}
        >
          {createVersion.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {t('history.save', { defaultValue: 'Save Version' })}
        </button>
      </div>
      <ScrollArea className="pb-8 h-full">
        {projectionStatus === 'loading' && <p role="status" className="text-xs text-muted-foreground mb-3">
          {t('history.projectionLoading', { defaultValue: 'Loading the permissioned historical chart… The current chart remains visible until ready.' })}
        </p>}
        {projectionStatus === 'unavailable' && <p role="alert" className="text-xs text-destructive mb-3">
          {t('history.projectionUnavailable', { defaultValue: 'A complete historical projection is unavailable for this version. The current chart is still shown; it is not a historical preview.' })}
        </p>}
        {projectionStatus === 'ready' && <p role="status" className="text-xs text-muted-foreground mb-3">
          {t('history.projectionReady', { defaultValue: 'Historical chart under your current data permissions. Restore changes diagram presentation only, not canonical organization facts.' })}
        </p>}
        <ul className="space-y-3 pr-2">
          {versions.isLoading && (<li className="text-xs text-muted-foreground">{t('history.loading', { defaultValue: 'Loading…' })}</li>)}
          {versions.isError && (<li className="text-xs text-destructive">{t('history.loadFailed', { defaultValue: 'Failed to load versions' })}</li>)}
          {versions.data?.length === 0 && !versions.isLoading && (<li className="text-xs text-muted-foreground">{t('history.empty', { defaultValue: 'No versions yet.' })}</li>)}
          {versions.data?.map((entry) => {
            const isPreviewing = activePreviewVersion === entry.version;
            const isPreviewLoading = previewLoadingVersion === entry.version;
            return (
              <li
                key={entry.version}
                className={`border rounded-md p-3 transition ${isPreviewing ? "border-primary/60 bg-primary/5" : ""}`}
              >
                <div className="flex items-center justify-between text-sm font-medium">
                  {entry.label && (<span>{translateVersionLabel(entry.label, t)}</span>)}
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true, locale: dateLocale })}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  <span>{t('history.versionN', { n: entry.version, defaultValue: 'Version {{n}}' })}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {entry.createdBy
                    ? t('history.byName', { name: userInfo.name, defaultValue: 'by {{name}}' })
                    : t('history.system', { defaultValue: 'System' })}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1 text-xs ${isPreviewing ? "text-primary" : "text-muted-foreground hover:text-primary"} disabled:cursor-not-allowed disabled:opacity-60`}
                    onClick={() => handlePreview(entry)}
                    disabled={
                      !doc ||
                      isPreviewLoading ||
                      (previewLoadingVersion !== null &&
                        previewLoadingVersion !== entry.version) ||
                      isRestoring
                    }
                  >
                    {isPreviewLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isPreviewing ? t('history.exitPreview', { defaultValue: 'Exit Preview' }) : t('history.preview', { defaultValue: 'Preview' })}
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => handleRename(entry)}
                    disabled={renameVersion.isPending}
                  >
                    <Pencil className="h-4 w-4" /> {t('history.rename', { defaultValue: 'Rename' })}
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => {
                      handleRestore(entry);
                    }}
                    disabled={restoreVersion.isPending || isRestoring}
                  >
                    <RotateCcw className="h-4 w-4" /> {t('history.restore', { defaultValue: 'Restore' })}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </ScrollArea>

      {/* Local Backups (this device) */}
      <div className="border-t pt-3 mt-3">
        <div className="text-xs font-semibold text-muted-foreground mb-2">
          {t('history.localBackups', { defaultValue: 'Local Backups (this device)' })}
        </div>
        {localBackups.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            {t('history.noLocalBackups', { defaultValue: 'No local backups yet.' })}
          </div>
        ) : (
          <ul className="space-y-2">
            {localBackups.map((backup) => {
              const triggerKeyMap: Record<string, string> = {
                periodic: 'history.backupTriggerPeriodic',
                visibility_hidden: 'history.backupTriggerTabHidden',
                before_unload: 'history.backupTriggerBeforeUnload',
                server_confirmed: 'history.backupTriggerServerConfirmed',
                manual: 'history.backupTriggerManual',
                pre_restore: 'history.backupTriggerPreRestore',
              };
              const triggerDefault: Record<string, string> = {
                periodic: 'auto-save',
                visibility_hidden: 'tab hidden',
                before_unload: 'before close',
                server_confirmed: 'server save',
                manual: 'manual',
                pre_restore: 'pre-restore',
              };
              const triggerLabelKey = triggerKeyMap[backup.trigger];
              const backupTriggerText = triggerLabelKey
                ? t(triggerLabelKey, { defaultValue: triggerDefault[backup.trigger] ?? backup.trigger })
                : backup.trigger;
              return (
                <li key={backup.id} className="border rounded-md p-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {formatDistanceToNow(backup.createdAt, { addSuffix: true, locale: dateLocale })}
                    </span>
                    <span className="text-muted-foreground">
                      {backupTriggerText}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                    <span>
                      {t('history.localBackupShapesCount', {
                        count: backup.docStats.shapesCount,
                        defaultValue: '{{count}} shapes',
                      })}
                    </span>
                    {backup.serverSynced && (
                      <span className="flex items-center gap-0.5 text-green-600">
                        <CheckCircle className="h-3 w-3" /> {t('history.localBackupSynced', { defaultValue: 'synced' })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => restoreLocalBackup(backup.id)}
                      disabled={isRestoringLocal || isRestoring}
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> {t('history.restore', { defaultValue: 'Restore' })}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                      onClick={() => downloadLocalBackup(backup.id)}
                    >
                      <Download className="h-3.5 w-3.5" /> {t('history.download', { defaultValue: 'Download' })}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Restore from backup file */}
      <div className="border-t pt-3 mt-3">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleRestoreFromFile}
          disabled={isRestoring || !doc}
        >
          <Upload className="h-3.5 w-3.5" />
          {t('history.restoreFromFile', { defaultValue: 'Restore from file…' })}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".crly,.ybin"
          className="hidden"
          onChange={handleFileSelected}
        />
      </div>

      {isRestoring && portalTarget
        ? createPortal(
            <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-3 rounded-md bg-background/90 px-6 py-4 shadow-lg">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span className="text-sm text-muted-foreground">{t('history.restoring', { defaultValue: 'Restoring version…' })}</span>
              </div>
            </div>,
            portalTarget
          )
        : null}
      <Dialog open={nameDialogOpen} onOpenChange={setNameDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              {nameDialogMode.type === 'save'
                ? t('history.saveTitle', { defaultValue: 'Save version' })
                : t('history.renameTitle', { defaultValue: 'Rename version' })}
            </DialogTitle>
            <DialogDescription>
              {nameDialogMode.type === 'save'
                ? t('history.saveDescription', { defaultValue: 'Give this version a name to find it later.' })
                : t('history.renameDescription', { defaultValue: 'Update the name for this version.' })}
            </DialogDescription>
          </DialogHeader>
          <Input
            ref={nameInputRef}
            autoFocus
            placeholder={t('history.namePlaceholder', { defaultValue: 'e.g. Before restructure' })}
            value={nameDialogValue}
            onChange={(e) => setNameDialogValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleNameDialogSubmit(); }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNameDialogOpen(false)}>
              {tCommon('actions.cancel')}
            </Button>
            <Button onClick={handleNameDialogSubmit}>
              {nameDialogMode.type === 'save'
                ? t('history.save', { defaultValue: 'Save Version' })
                : t('history.rename', { defaultValue: 'Rename' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

DiagramHistoryPanel.displayName = "DiagramHistoryPanel";
